import { jsx } from '@amazon/vinyl-tsx'
import type { ObservableValue } from '@amazon/vinyl-observable'

/**
 * Reactive conditional MOUNT.
 *
 * `visible={obs}` in this vinyl-tsx fork only toggles `display:none` — the
 * subtree, its live event handlers, and any fetches its children fire on mount
 * all stay in the DOM for every viewer, merely hidden. Use `mountWhen` when a
 * subtree must be truly ABSENT (not just hidden) while the condition is false:
 * it constructs `factory()` and attaches it only while `when$` is true, and
 * removes it (firing the subtree's `onConnect` disconnect disposers) when
 * false. The host is `display:contents`, so it contributes no layout box.
 *
 * `factory` is invoked fresh on each true transition, so it must be safe to
 * re-run (each mount is a new instance with its own state).
 */
export function mountWhen(when$: ObservableValue<boolean>, factory: () => Node): HTMLElement {
    return (
        <div
            style={{ display: 'contents' }}
            onConnect={(host) => {
                let mounted: ChildNode | null = null
                const clear = () => {
                    if (mounted) {
                        mounted.remove()
                        mounted = null
                    }
                }
                const sub = when$.onData((show) => {
                    if (show) {
                        if (!mounted) host.appendChild((mounted = factory() as ChildNode))
                    } else {
                        clear()
                    }
                })
                return () => {
                    sub()
                    clear()
                }
            }}
        />
    ) as HTMLElement
}
